import React, { useEffect, useState } from 'react';
import { SaveIcon, GlobeIcon } from 'lucide-react';
export function CurrencyDetail({
  currency,
  onSave,
  onCancel,
  isCreating,
  locale
}) {
  const [formData, setFormData] = useState({
    currencyId: '',
    code: '',
    numericCode: '',
    name: '',
    symbol: '',
    countries: [],
    isDefault: false,
    decimalPlaces: 2
  });
  // Common country options for dropdown
  const commonCountries = ['United States', 'United Kingdom', 'Germany', 'France', 'Italy', 'Spain', 'Japan', 'China', 'India', 'Canada', 'Australia', 'Brazil', 'Russia', 'South Africa', 'Mexico', 'Singapore'];
  useEffect(() => {
    if (currency && !isCreating) {
      setFormData({
        ...currency,
        countries: [...currency.countries] // Create a copy of the array
      });
    } else {
      // Default values for new currency
      setFormData({
        currencyId: '',
        code: '',
        numericCode: '',
        name: '',
        symbol: '',
        countries: [],
        isDefault: false,
        decimalPlaces: 2
      });
    }
  }, [currency, isCreating]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleNumericChange = e => {
    const {
      name,
      value
    } = e.target;
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue)) {
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else if (value === '') {
      setFormData({
        ...formData,
        [name]: ''
      });
    }
  };
  const handleCountrySelect = e => {
    const selectedCountry = e.target.value;
    if (selectedCountry && !formData.countries.includes(selectedCountry)) {
      setFormData({
        ...formData,
        countries: [...formData.countries, selectedCountry]
      });
    }
  };
  const handleRemoveCountry = country => {
    setFormData({
      ...formData,
      countries: formData.countries.filter(c => c !== country)
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-14820">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-14821">
        <div data-id="element-14822">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14823">
            {isCreating ? 'Add New Currency' : 'Edit Currency'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-14824">
            {isCreating ? 'Enter details for the new currency' : 'Update currency details'}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-14825">
          <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14826">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14827">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-14828" />
            Save Currency
          </button>
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-14829">
        <form className="space-y-6" onSubmit={handleSubmit} data-id="element-14830">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-14831">
            {/* Basic Information */}
            <div className="sm:col-span-6" data-id="element-14832">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14833">
                Basic Information
              </h4>
            </div>
            <div className="sm:col-span-3" data-id="element-14834">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-14835">
                Currency Name
              </label>
              <div className="mt-1" data-id="element-14836">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14837" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-14838">
              <label htmlFor="symbol" className="block text-sm font-medium text-gray-700" data-id="element-14839">
                Symbol
              </label>
              <div className="mt-1" data-id="element-14840">
                <input type="text" name="symbol" id="symbol" value={formData.symbol} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14841" />
              </div>
            </div>
            {/* ISO Codes */}
            <div className="sm:col-span-6" data-id="element-14842">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14843">
                ISO Codes
              </h4>
            </div>
            <div className="sm:col-span-3" data-id="element-14844">
              <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-14845">
                Currency Code (ISO 4217)
              </label>
              <div className="mt-1" data-id="element-14846">
                <input type="text" name="code" id="code" value={formData.code} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required disabled={!isCreating} // Currency code cannot be changed after creation
              data-id="element-14847" />
              </div>
              {!isCreating && <p className="mt-1 text-xs text-gray-500" data-id="element-14848">
                  Currency code cannot be changed after creation
                </p>}
            </div>
            <div className="sm:col-span-3" data-id="element-14849">
              <label htmlFor="numericCode" className="block text-sm font-medium text-gray-700" data-id="element-14850">
                Numeric Code (ISO 4217)
              </label>
              <div className="mt-1" data-id="element-14851">
                <input type="text" name="numericCode" id="numericCode" value={formData.numericCode} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14852" />
              </div>
            </div>
            {/* Formatting */}
            <div className="sm:col-span-6" data-id="element-14853">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14854">
                Formatting
              </h4>
            </div>
            <div className="sm:col-span-3" data-id="element-14855">
              <div className="flex items-center h-full pt-6" data-id="element-14856">
                <input id="isDefault" name="isDefault" type="checkbox" checked={formData.isDefault} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-14857" />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700" data-id="element-14858">
                  Set as default currency
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500 pl-6" data-id="element-14859">
                {formData.isDefault ? 'This will be the default currency for new transactions' : 'Check to make this the default currency'}
              </p>
            </div>
            {/* Countries */}
            <div className="sm:col-span-6" data-id="element-14860">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14861">
                Countries Using This Currency
              </h4>
            </div>
            <div className="sm:col-span-3" data-id="element-14862">
              <label htmlFor="countrySelect" className="block text-sm font-medium text-gray-700" data-id="element-14863">
                Add Country
              </label>
              <div className="mt-1" data-id="element-14864">
                <select id="countrySelect" name="countrySelect" value="" onChange={handleCountrySelect} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-14865">
                  <option value="" data-id="element-14866">Select a country</option>
                  {commonCountries.map(country => <option key={country} value={country} data-id="element-14867">
                      {country}
                    </option>)}
                  <option value="Other" data-id="element-14868">Other (type manually)</option>
                </select>
              </div>
            </div>
            {/* Custom country input */}
            {formData.countries.includes('Other') && <div className="sm:col-span-3" data-id="element-14869">
                <label htmlFor="customCountry" className="block text-sm font-medium text-gray-700" data-id="element-14870">
                  Custom Country Name
                </label>
                <div className="mt-1 flex" data-id="element-14871">
                  <input type="text" id="customCountry" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter country name" data-id="element-14872" />
                  <button type="button" className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {
                const input = document.getElementById('customCountry');
                if (input.value) {
                  setFormData({
                    ...formData,
                    countries: [...formData.countries.filter(c => c !== 'Other'), input.value]
                  });
                  input.value = '';
                }
              }} data-id="element-14873">
                    Add
                  </button>
                </div>
              </div>}
            {/* Selected countries */}
            <div className="sm:col-span-6" data-id="element-14874">
              <div className="mt-1" data-id="element-14875">
                {formData.countries.length === 0 ? <p className="text-sm text-gray-500" data-id="element-14876">
                    No countries selected. Please add at least one country that
                    uses this currency.
                  </p> : <div className="flex flex-wrap gap-2" data-id="element-14877">
                    {formData.countries.map(country => <div key={country} className="inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-14878">
                        <GlobeIcon className="h-3 w-3 mr-1" data-id="element-14879" />
                        {country}
                        <button type="button" className="ml-1 text-indigo-600 hover:text-indigo-900 focus:outline-none" onClick={() => handleRemoveCountry(country)} data-id="element-14880">
                          &times;
                        </button>
                      </div>)}
                  </div>}
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-14881">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14882">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14883">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-14884" />
              {isCreating ? 'Create Currency' : 'Update Currency'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}